import { Dispatch, FC, SetStateAction } from "react";
import Drawer from "react-modern-drawer";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CheckboxCard,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input
} from "shared/components";
import { Loader } from "shared/components/Loader";
import { useMedia } from "shared/providers";
import { XClose } from "untitledui-js-base";
import { useVerificationTemplatesController } from "views/verification-templates/controller";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { useForm } from "shared/hooks/use-form";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { PropertyGroup } from "types/property-group";

interface PropertyGroupSettingsProps {
  open: boolean;
  onClose: () => void;
  onShowTemplateReviewModal: () => void;
  setSelectedTemplateId: Dispatch<SetStateAction<string | null>>;
  selectedTemplateId: string | null;
  propertyGroup?: PropertyGroup;
}

export const PropertyGroupSettings: FC<PropertyGroupSettingsProps> = ({
  open,
  onClose,
  onShowTemplateReviewModal,
  setSelectedTemplateId,
  selectedTemplateId,
  propertyGroup
}) => {
  const { Property } = useUnitsPropertiesDictionary();
  const { isMobile } = useMedia();
  const { templates, isLoading: isLoadingTemplates } =
    useVerificationTemplatesController(999);
  const queryClient = useQueryClient();
  const { register, onSubmit, values } = useForm({
    groupName: propertyGroup?.name || ""
  });

  const { mutate: updatePropertyGroup, isPending: isUpdatingPropertyGroup } =
    useMutation({
      mutationKey: ["patch_property_group", String(propertyGroup?.id)],
      mutationFn: async (data: {
        name: string;
        verification_template_identifier: string;
      }) => {
        await instance.patch(
          `/portal/v1/property_groups/${propertyGroup?.id}`,
          data
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["property_group", String(propertyGroup?.id)]
        });
        toast.success("Property group updated successfully");
        onClose();
      },
      onError: (error) => {
        toast.error(getErrorMessage(error));
      }
    });

  const sortedTemplates = templates?.sort(
    (a, b) => Number(b.default) - Number(a.default)
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size={isMobile ? 350 : 500}
      zIndex={20}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-alpha-80">
        <h1 className="text-xl font-bold text-content-1000">
          {Property} group settings
        </h1>
        <IconButton
          variant={IconButtonVariant.BASE}
          size={IconButtonSize.SM}
          borderLess
          className="!justify-end"
          onClick={onClose}
          Icon={XClose}
        />
      </div>

      <form
        className="flex flex-col h-[calc(100vh-64px)]"
        onSubmit={onSubmit({
          onSuccess: () => {
            updatePropertyGroup({
              name: values.groupName,
              verification_template_identifier: selectedTemplateId || ""
            });
          }
        })}
      >
        <div className="px-6 pt-6 flex-1 overflow-y-auto">
          <Input
            label="Group name"
            id="group-name"
            required
            placeholder="Group name"
            {...register("groupName")}
          />

          {isLoadingTemplates ? (
            <div className="flex items-center justify-center w-full h-[calc(100vh-266px)]">
              <Loader />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 mt-4 pt-6 border-t border-alpha-40">
              {sortedTemplates?.map((template) => (
                <CheckboxCard
                  key={template.id}
                  checked={selectedTemplateId === template.id}
                  id={template.id}
                  label={
                    <div className="relative w-full gap-2">
                      {template.default ? (
                        <p className="absolute -right-4 -top-4 rounded-bl-lg rounded-tr-xl border-b border-l border-alpha-100 bg-white px-2 text-xs font-medium">
                          Default
                        </p>
                      ) : null}
                      <p className="w-full overflow-hidden text-ellipsis whitespace-nowrap break-all text-sm font-medium text-content-600">
                        {template.name}
                      </p>
                      <Button
                        variant={ButtonVariant.TEXT}
                        size={ButtonSize.SM}
                        onClick={() => {
                          setSelectedTemplateId(template.id);
                          onShowTemplateReviewModal();
                        }}
                      >
                        Review
                      </Button>
                    </div>
                  }
                  name="name"
                  onChange={() => setSelectedTemplateId(template.id)}
                  className="flex min-w-[207px] justify-start p-[18px]"
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-2 px-6 py-4 border-t border-alpha-100">
          <div className="w-full">
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              className="w-full"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          <div className="w-full">
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              loading={isUpdatingPropertyGroup}
              disabled={isLoadingTemplates}
              className="w-full"
              type="submit"
            >
              Save
            </Button>
          </div>
        </div>
      </form>
    </Drawer>
  );
};
